import { useEffect, useMemo, useState } from 'react'
import { Search, Eye, Package, Layers } from 'lucide-react'
import { supabase, isDemoMode } from '../../lib/supabase'
import { DemoNotice } from '../../lib/demo'

// Visibility matrix: which products are on which sites.
//
// Under the hood we toggle card_products against each site's
// "Standard order card" (auto-created by the seed). Ticking a box
// inserts a card_products row; unticking removes it.

const DEFAULT_CARD = 'Standard order card'

export default function ProductVisibility() {
  const [products, setProducts] = useState([])
  const [groups, setGroups] = useState([])
  const [sites, setSites] = useState([])
  const [cardByCompany, setCardByCompany] = useState({}) // company_id -> card_id
  const [matrix, setMatrix] = useState(new Set())        // Set of "cardId::productId" present
  const [loading, setLoading] = useState(false)
  const [q, setQ] = useState('')
  const [groupFilter, setGroupFilter] = useState('')

  const load = async () => {
    if (isDemoMode) return
    setLoading(true)
    const [pRes, gRes, cRes, kRes] = await Promise.all([
      supabase.from('products').select('id, sku, name, unit, pack_size, price').is('archived_at', null).order('name'),
      supabase.from('client_groups').select('id, name').is('archived_at', null).order('name'),
      supabase.from('companies').select('id, name, client_group_id').is('archived_at', null).order('name'),
      supabase.from('cards').select('id, name, company_id').eq('name', DEFAULT_CARD).is('archived_at', null),
    ])
    setProducts(pRes.data || [])
    setGroups(gRes.data || [])
    setSites(cRes.data || [])
    const cmap = {}
    for (const k of kRes.data || []) cmap[k.company_id] = k.id
    setCardByCompany(cmap)

    // Load existing card_products for those cards.
    const cardIds = Object.values(cmap)
    if (cardIds.length > 0) {
      const { data: cps } = await supabase
        .from('card_products').select('card_id, product_id')
        .in('card_id', cardIds)
      const set = new Set((cps || []).map(r => `${r.card_id}::${r.product_id}`))
      setMatrix(set)
    } else {
      setMatrix(new Set())
    }
    setLoading(false)
  }
  useEffect(() => { load() }, [])

  const filteredSites = useMemo(() => {
    if (!groupFilter) return sites
    return sites.filter(s => s.client_group_id === groupFilter)
  }, [sites, groupFilter])

  const filteredProducts = useMemo(() => {
    const s = q.toLowerCase()
    if (!s) return products
    return products.filter(p => p.name.toLowerCase().includes(s) || p.sku.toLowerCase().includes(s))
  }, [products, q])

  const ensureCard = async (companyId) => {
    if (cardByCompany[companyId]) return cardByCompany[companyId]
    const { data, error } = await supabase.from('cards').insert({ company_id: companyId, name: DEFAULT_CARD }).select().single()
    if (error) { alert(error.message); return null }
    setCardByCompany(prev => ({ ...prev, [companyId]: data.id }))
    return data.id
  }

  const toggle = async (siteId, productId) => {
    if (isDemoMode) return
    let cardId = cardByCompany[siteId]
    if (!cardId) cardId = await ensureCard(siteId)
    if (!cardId) return
    const key = `${cardId}::${productId}`
    const present = matrix.has(key)
    // Optimistic update
    setMatrix(prev => {
      const next = new Set(prev)
      if (present) next.delete(key); else next.add(key)
      return next
    })
    if (present) {
      const { error } = await supabase.from('card_products').delete().eq('card_id', cardId).eq('product_id', productId)
      if (error) { alert(error.message); load() }
    } else {
      const { error } = await supabase.from('card_products').insert({ card_id: cardId, product_id: productId })
      if (error) { alert(error.message); load() }
    }
  }

  const bulkSetForProduct = async (productId, on) => {
    if (isDemoMode) return
    if (!confirm(`${on ? 'Enable' : 'Disable'} this product for all ${filteredSites.length} visible site${filteredSites.length === 1 ? '' : 's'}?`)) return
    for (const s of filteredSites) {
      let cardId = cardByCompany[s.id]
      if (!cardId) cardId = await ensureCard(s.id)
      if (!cardId) continue
      const key = `${cardId}::${productId}`
      const present = matrix.has(key)
      if (on && !present) {
        await supabase.from('card_products').insert({ card_id: cardId, product_id: productId })
      } else if (!on && present) {
        await supabase.from('card_products').delete().eq('card_id', cardId).eq('product_id', productId)
      }
    }
    load()
  }

  if (loading) return <div className="card"><p className="text-sm text-gray-500">Loading matrix…</p></div>

  return (
    <div className="space-y-4">
      <DemoNotice />

      <div className="card">
        <div className="flex items-center gap-2 mb-2">
          <Eye className="w-5 h-5 text-brand" />
          <h3 className="font-semibold text-gray-800">Product Visibility</h3>
        </div>
        <p className="text-sm text-gray-500 mb-4">
          Tick a box to make a product visible on that site's order page. Ticked products land on the site's <em>{DEFAULT_CARD}</em>; per-card price &amp; description overrides happen on each site's card.
        </p>

        <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
          <div className="relative flex-1 max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input type="text" value={q} onChange={e => setQ(e.target.value)}
                   placeholder="Filter products…" className="input-field pl-9" />
          </div>
          <select value={groupFilter} onChange={e => setGroupFilter(e.target.value)} className="input-field max-w-xs">
            <option value="">All groups ({sites.length} sites)</option>
            {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
          </select>
        </div>
      </div>

      {products.length === 0 ? (
        <div className="card text-sm text-gray-500">
          No products yet. Add some on the <a className="text-brand-dark underline" href="/products">Products</a> page first.
        </div>
      ) : filteredSites.length === 0 ? (
        <div className="card text-sm text-gray-500">No sites match the current filter.</div>
      ) : (
        <div className="card overflow-x-auto">
          <table className="text-sm border-collapse" style={{ minWidth: '100%' }}>
            <thead>
              <tr>
                <th className="sticky left-0 bg-white text-left p-3 font-medium text-gray-700 border border-gray-200" style={{ width: 320, minWidth: 320 }}>
                  Product
                </th>
                {filteredSites.map(s => (
                  <th key={s.id} className="p-2 align-bottom border border-gray-200 bg-gray-50" style={{ width: 64, minWidth: 64, height: 260 }}>
                    <div className="text-xs text-gray-700 font-medium mx-auto" style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', height: 240, whiteSpace: 'nowrap' }}>
                      {s.name}
                    </div>
                  </th>
                ))}
                <th className="p-2 text-xs text-gray-500 border border-gray-200 bg-gray-50" style={{ width: 110, minWidth: 110 }}>All</th>
              </tr>
            </thead>
            <tbody>
              {filteredProducts.map((p, rowIdx) => (
                <tr key={p.id} className={rowIdx % 2 === 0 ? 'bg-white hover:bg-brand-50/40' : 'bg-gray-50/40 hover:bg-brand-50/40'}>
                  <td className="sticky left-0 p-3 border border-gray-200" style={{ width: 320, minWidth: 320, background: 'inherit' }}>
                    <div className="flex items-center gap-3">
                      <Package className="w-5 h-5 text-brand shrink-0" />
                      <div className="min-w-0">
                        <div className="font-medium text-gray-800 truncate">{p.name}</div>
                        <div className="text-xs text-gray-500 truncate">
                          {p.sku}
                          {p.unit ? ` · ${p.unit}` : ''}
                          {p.pack_size ? ` · ${p.pack_size}/box` : ''}
                          {p.price != null ? ` · £${Number(p.price).toFixed(2)}` : ''}
                        </div>
                      </div>
                    </div>
                  </td>
                  {filteredSites.map(s => {
                    const cardId = cardByCompany[s.id]
                    const checked = cardId ? matrix.has(`${cardId}::${p.id}`) : false
                    return (
                      <td key={s.id} className="p-0 text-center border border-gray-200" style={{ width: 64, minWidth: 64 }}>
                        <label className="block py-3 cursor-pointer">
                          <input
                            type="checkbox" checked={checked}
                            onChange={() => toggle(s.id, p.id)}
                            className="w-5 h-5 accent-brand"
                          />
                        </label>
                      </td>
                    )
                  })}
                  <td className="p-2 text-center whitespace-nowrap border border-gray-200" style={{ width: 110, minWidth: 110 }}>
                    <button onClick={() => bulkSetForProduct(p.id, true)}  className="text-xs text-brand-dark hover:underline px-1.5 py-0.5">all</button>
                    <span className="text-gray-300">|</span>
                    <button onClick={() => bulkSetForProduct(p.id, false)} className="text-xs text-red-600 hover:underline px-1.5 py-0.5">none</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
